import { NUMBER_OF_QUANTITY_OPTIONS } from "@faire/deprecated-retailer-visitor-shared/views/product/ProductDetails/AddToCart/useProductAddToCartState";
import { Core } from "@faire/design-tokens";
import { LoadingSpinner } from "@faire/slate/components/loading-spinner";
import { trackRetailersRetailerSelectCustomOptionClick } from "@faire/web-api--source/events/retailers/click/retailerSelectCustomOption";
import { trackRetailersSelectQuantityDropdownClick } from "@faire/web-api--source/events/retailers/click/selectQuantityDropdown";
import { useViewport } from "@faire/web/common/hooks/useViewport";
import { useStrictLocalization } from "@faire/web/common/localization";
import { IOption, Dropdown } from "@faire/web/slate-deprecated/Dropdown";
import { ICreateNewOption } from "@faire/web/slate-deprecated/Dropdown/types";
import { InputWrapper } from "@faire/web/slate-deprecated/Input";
import { MeasurementInput } from "@faire/web/slate/Input/MeasurementInput";
import { useState, useEffect, useMemo, useCallback } from "react";
import * as React from "react";
import styled, { css } from "styled-components";

const MINIMUM_INPUT_WIDTH = 75;
const SMALL_MIN_INPUT_WIDTH = 50;
const HORIZONTAL_PADDING = 16;

type Props = {
  value: string;
  placeholder?: string;
  options: IOption[];
  onSelect: ({ value }: { value: string }) => void;
  loading?: boolean;
  disabled?: boolean;
  containingElement?: HTMLElement;
  className?: string;
  dataTestId?: string;
  quantityTextAlign?: "left" | "center";
  dynamicWidth?: boolean;
  shouldUseCustomOption?: boolean;
  getCustomOptionLabel?: (value: number) => string;
  getCustomOptionValue?: (quantity: number) => string;
  small?: boolean;
  isCustomQuantityDropdown?: boolean;
};

export const QuantityOrOptionPicker: React.FunctionComponent<Props> = ({
  value,
  options,
  className,
  quantityTextAlign,
  dynamicWidth,
  shouldUseCustomOption,
  getCustomOptionLabel,
  getCustomOptionValue,
  small,
  isCustomQuantityDropdown,
  ...props
}) => {
  const { isTabletAndBelow } = useViewport();
  const { strictLocalize } = useStrictLocalization();
  const [hasEditedCustomOption, setHasEditedCustomOption] = useState(false);
  const [customOption, setCustomOption] = useState<IOption>({
    value: "",
    label: strictLocalize({
      id: "dhKCUV",
      defaultMessage: "Custom Quantity",
      description: {
        text: "A line of text indicating user a interactable button for entering custom quantity.",
        img: "https://cdn.faire.com/fastly/b89215865d97abef2af83c425de23ad5c51331009916e831aedab85374ed9c4c.png",
      },
    }),
  });
  const [customQuantityInput, setCustomQuantityInput] = useState<
    IOption | undefined
  >(undefined);

  const isValueInOptions = useCallback(
    (value: string): boolean => {
      const useAllOptions = options.length < NUMBER_OF_QUANTITY_OPTIONS;
      const _options = useAllOptions ? options : options.slice(0, -1);
      return !!_options.find(
        (option) => option.value === value || value === customOption.value
      );
    },
    [customOption.value, options]
  );

  useEffect(() => {
    if (!isValueInOptions(value) && shouldUseCustomOption) {
      setCustomOption({
        value,
        label: getCustomOptionLabel?.(parseInt(value)) ?? "",
      });
      setHasEditedCustomOption(true);
    }
  }, [getCustomOptionLabel, isValueInOptions, shouldUseCustomOption, value]);

  useEffect(() => {
    if (
      isCustomQuantityDropdown &&
      !isValueInOptions(value) &&
      shouldUseCustomOption
    ) {
      setCustomQuantityInput({
        value: value ?? "",
        label: getCustomOptionLabel?.(parseInt(value)) ?? "",
      });
    }
  }, [
    getCustomOptionLabel,
    isValueInOptions,
    isCustomQuantityDropdown,
    shouldUseCustomOption,
    value,
  ]);

  const createNewOption: ICreateNewOption = {
    label: (searchValue) => searchValue,
    onClick: (value) => {
      const quantity = parseInt(value ?? "0");
      if (!isNaN(quantity)) {
        const customOptionValue =
          getCustomOptionValue?.(quantity) ?? value ?? "";
        setCustomQuantityInput?.({
          value: customOptionValue,
          label: getCustomOptionLabel?.(quantity) ?? "",
        });
        props.onSelect({ value: customOptionValue });
      }
    },
    hideUnlessNoOptions: true,
    closeDropdown: true,
  };

  const quantityPickerWidth = useMemo(() => {
    const minWidth = small ? SMALL_MIN_INPUT_WIDTH : MINIMUM_INPUT_WIDTH;
    const currentWidth =
      value === undefined
        ? minWidth
        : value.toString().length * 10 + 3 * HORIZONTAL_PADDING;
    return Math.max(currentWidth, minWidth);
  }, [small, value]);

  const quantityAlignRight = quantityTextAlign === "left" && isTabletAndBelow;

  return (
    <Wrapper
      className={className}
      $dynamicWidth={dynamicWidth}
      $width={quantityPickerWidth + "px"}
      $quantityAlignRight={quantityAlignRight}
      $minWidth={small ? SMALL_MIN_INPUT_WIDTH : MINIMUM_INPUT_WIDTH}
    >
      <QuantityDropdown
        {...props}
        value={value}
        options={options}
        quantityTextAlign={quantityTextAlign}
        shouldUseCustomOption={shouldUseCustomOption}
        small={small}
        hasEditedCustomOption={hasEditedCustomOption}
        customOption={customOption}
        isCustomQuantityDropdown={isCustomQuantityDropdown ?? false}
        customQuantityInput={customQuantityInput}
        createNewOption={createNewOption}
      />
    </Wrapper>
  );
};

type DropdownProps = Pick<
  Props,
  | "value"
  | "placeholder"
  | "options"
  | "onSelect"
  | "loading"
  | "disabled"
  | "containingElement"
  | "dataTestId"
  | "quantityTextAlign"
  | "shouldUseCustomOption"
  | "small"
> & {
  hasEditedCustomOption: boolean;
  customOption: IOption;
  isCustomQuantityDropdown: boolean;
  customQuantityInput?: IOption;
  createNewOption: ICreateNewOption;
};

const QuantityDropdown: React.FC<DropdownProps> = ({
  value,
  placeholder,
  options,
  onSelect,
  loading,
  disabled,
  containingElement,
  dataTestId,
  quantityTextAlign,
  shouldUseCustomOption,
  small,
  hasEditedCustomOption,
  customOption,
  isCustomQuantityDropdown,
  customQuantityInput,
  createNewOption,
}) => {
  const { strictLocalize } = useStrictLocalization();
  const [customQuantitySelection, setCustomQuantitySelection] = useState(false);

  const handleOnCustomOptionChange = (value: string) => {
    trackRetailersRetailerSelectCustomOptionClick(value);
    handleChangeValue(value);
  };

  const handleDropdownSelect = (value: string) => {
    trackRetailersSelectQuantityDropdownClick(value);
    handleChangeValue(value);
  };

  const handleChangeValue = (value: string | number | undefined) => {
    const lastOptionValue = options[options.length - 1]?.value ?? "";
    const newValue = value === undefined ? lastOptionValue : String(value);
    if (customQuantitySelection) {
      setCustomQuantitySelection(false);
    } else if (value === lastOptionValue) {
      setCustomQuantitySelection(true);
    }
    onSelect({ value: newValue });
  };

  const hideCustomOptionSelector = options.length < NUMBER_OF_QUANTITY_OPTIONS;
  const shouldShowCustomOptionSelector =
    !hideCustomOptionSelector && !isCustomQuantityDropdown;

  const optionsList = useMemo(() => {
    const optionsListForCustomQuantity = hideCustomOptionSelector
      ? options
      : options.slice(0, -1);
    if (
      isCustomQuantityDropdown &&
      customQuantityInput &&
      !optionsListForCustomQuantity.find(
        (option) => option.value === customQuantityInput.value
      )
    ) {
      return [...optionsListForCustomQuantity, customQuantityInput];
    }
    return optionsListForCustomQuantity;
  }, [
    customQuantityInput,
    hideCustomOptionSelector,
    isCustomQuantityDropdown,
    options,
  ]);

  if ((shouldUseCustomOption && !disabled) || isCustomQuantityDropdown) {
    return (
      <StyledCustomDropdown
        value={value}
        data-test-id={dataTestId}
        options={optionsList}
        onSelect={({ value }) => handleDropdownSelect(value)}
        placeholder={
          !hideCustomOptionSelector
            ? strictLocalize({
                id: "bwnOM9",
                defaultMessage: "Enter Custom Quantity",
                description: {
                  text: "placeholder letting user know that they can enter a custom quantity",
                },
              })
            : undefined
        }
        containingElement={containingElement}
        customOption={
          shouldShowCustomOptionSelector
            ? {
                ...customOption,
                onValueChange: handleOnCustomOptionChange,
                hasEdited: hasEditedCustomOption,
              }
            : undefined
        }
        filter={isCustomQuantityDropdown}
        createNewOption={isCustomQuantityDropdown ? createNewOption : undefined}
        useTabletAndAboveDropdownStyle
      />
    );
  } else if (loading) {
    return <LoadingSpinner size="small" />;
  } else if (customQuantitySelection) {
    return (
      <StyledNumberInput
        data-test-id="custom-quantity-number-input"
        disabled={disabled}
        value={parseInt(value)}
        onChange={(newValue) => handleChangeValue(newValue)}
        onClick={(event) => {
          event.preventDefault();
        }}
      />
    );
  } else {
    return (
      <StyledDropdown
        disabled={disabled}
        data-test-id={dataTestId}
        options={options}
        onSelect={({ value }) => handleDropdownSelect(value)}
        placeholder={placeholder ? placeholder : value}
        containingElement={containingElement}
        $quantityTextAlign={quantityTextAlign}
        $smallPadding={small}
        disableInput
        useTabletAndAboveDropdownStyle
      />
    );
  }
};

const Wrapper = styled.div<{
  $dynamicWidth: boolean | undefined;
  $width: string;
  $quantityAlignRight: boolean | undefined;
  $minWidth: number;
}>`
  min-width: ${(props) => props.$minWidth}px;
  width: ${(props) => (props.$dynamicWidth ? props.$width : "100%")};
  ${(props) => (props.$quantityAlignRight ? "margin-left: auto;" : "")}
`;

const StyledDropdown = styled(Dropdown)<{
  $quantityTextAlign: "left" | "center" | undefined;
  $smallPadding: boolean | undefined;
}>`
  input,
  select {
    text-align: ${(props) => props.$quantityTextAlign ?? "center"};

    &::placeholder {
      color: ${Core.text.default};
    }

    caret-color: transparent;
  }
  ${(props) =>
    props.$smallPadding
      ? css`
          ${InputWrapper} {
            padding: 0 12px;
          }
        `
      : ""}

  flex-grow: 2;
`;
const StyledCustomDropdown = styled(Dropdown)`
  input {
    text-align: center;
  }
  select {
    &::placeholder {
      color: ${Core.text.default};
    }

    caret-color: transparent;
  }

  flex-grow: 2;
`;

/**
 * @trackcomponent
 * @supersededBy "@faire/web/slate/Input/MeasurementInput/MeasurementInput#MeasurementInput"
 */
const StyledNumberInput = styled(MeasurementInput)`
  input {
    width: 100%;
    text-align: center;
    padding-right: ${HORIZONTAL_PADDING}px;
  }
`;
